/** How a grid is read rather than anything it holds, so it lives in the reader's own browser. */
// `outOfPocket` has no caller until slice 8 (#540), whose OutOfPocketCard folds on its own key.
export type FoldScope = 'chapters' | 'outOfPocket' | 'incidents';

/** Both answers are written: missing is "nobody has said", which is not the same as open. */
export type Folds = Record<string, boolean>;

// The chapters keep their old key, so nobody's folds are forgotten by this becoming scoped.
export const foldsStorageKey = (productId: string, scope: FoldScope = 'chapters'): string =>
  scope === 'chapters' ? `mock:productFoldedChapters:${productId}` : `mock:productFolded:${scope}:${productId}`;

// localStorage throws in a private window or under an enterprise policy: read as nothing answered.
export const readFolds = (productId: string, scope: FoldScope = 'chapters'): Folds => {
  try {
    const raw = localStorage.getItem(foldsStorageKey(productId, scope));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    // The plain list this used to be written as, so an old fold is still found folded.
    if (Array.isArray(parsed)) {
      return Object.fromEntries(parsed.filter((key): key is string => typeof key === 'string').map(key => [key, true]));
    }
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(Object.entries(parsed).filter(([, shut]) => typeof shut === 'boolean'));
  } catch {
    return {};
  }
};

/** Best effort. A fold that cannot be written is still folded for as long as the tab is open. */
export const rememberFolds = (productId: string, folds: Folds, scope: FoldScope = 'chapters'): void => {
  try {
    localStorage.setItem(foldsStorageKey(productId, scope), JSON.stringify(folds));
  } catch {
    // The fold is React state either way; only remembering it across a reload is lost.
  }
};

/** Whether a block is folded, and what it does while nobody has said. */
export const isFolded = (folds: Folds, key: string, whenUnanswered = false): boolean => folds[key] ?? whenUnanswered;

/** One block's own answer, since a block that is switched off should not silently unfold. */
export const setFold = (folds: Folds, key: string, shut: boolean): Folds => ({ ...folds, [key]: shut });
